import i18n from '@/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { Message } from '@/types/api';
import { onAppEvent } from '@/lib/appEvents';
import { humanizeApiError } from '@/lib/humanizeApiError';

/**
 * Live pinned-messages cache for a channel.
 *
 * Both `PinnedBar` (sticky strip under the InfoBar) and
 * `PinnedMessagesButton` (popover from the pin icon) used to ship
 * near-identical fetch + `chat:pins-changed` listener copies; this
 * hook is the shared implementation.
 *
 * Returns `{ messages, loading, error, refetch }`. The error surface
 * is preserved so consumers can tell "empty list" from "fetch failed"
 * (Codex review #2 on commit 98a9f63: an earlier version flattened
 * failures to `messages=[]` which read as "no pins" in the UI).
 *
 * `enabled=false` skips the network call entirely — useful for a
 * popover whose trigger renders before the popover opens.
 */
export interface PinnedMessagesState {
  messages: Message[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePinnedMessages(
  channelId: string,
  enabled: boolean = true,
): PinnedMessagesState {
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Cross-fetch cancellation ref. Rapid pin/unpin events fire refetch()
  // many times; without this an older response could land after a newer
  // one and "stomp" it (stale list shown until the next event). The
  // ref holds the cancel-thunk for the in-flight call; each new refetch
  // invokes the previous one first.
  const inFlightCancelRef = useRef<(() => void) | null>(null);

  const refetch = useCallback((): void => {
    if (!enabled) return;
    inFlightCancelRef.current?.();
    let cancelled = false;
    inFlightCancelRef.current = () => {
      cancelled = true;
    };
    setLoading(true);
    setError(null);
    api
      .listPinnedMessages(channelId)
      .then((res) => {
        if (cancelled) return;
        setMessages(res.messages);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(humanizeApiError(err, i18n.t('common.loadFailed')).title);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        inFlightCancelRef.current = null;
      });
  }, [channelId, enabled]);

  useEffect(() => {
    if (!enabled) {
      // Tear down any in-flight call from a previous open=true cycle.
      inFlightCancelRef.current?.();
      inFlightCancelRef.current = null;
      setMessages(null);
      setError(null);
      setLoading(false);
      return;
    }
    setMessages(null);
    setError(null);
    refetch();
    const unsubPins = onAppEvent('chat:pins-changed', refetch);
    return () => {
      inFlightCancelRef.current?.();
      inFlightCancelRef.current = null;
      unsubPins();
    };
  }, [refetch, enabled]);

  return { messages, loading, error, refetch };
}
